// ====================================
// CONFIGURACIÓN DE LA APLICACIÓN
// ====================================

export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'Camping System',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  api_base_url: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  max_file_size_mb: Number(import.meta.env.VITE_MAX_FILE_SIZE_MB) || 10,
  allowed_file_types: import.meta.env.VITE_ALLOWED_FILE_TYPES?.split(',') || ['.jpg', '.jpeg', '.png', '.gif'],
  debug: import.meta.env.VITE_DEBUG === 'true',
} as const;

// ====================================
// CONFIGURACIÓN UAU
// ====================================

export const UAU_CONFIG = {
  BASE_URL: import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1',
  APPLICATION_SLUG: import.meta.env.VITE_UAU_APPLICATION_SLUG || 'camping',

  ENDPOINTS: {
    LOGIN: `${import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1'}/auth/login`,
    LOGOUT: `${import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1'}/auth/logout`,
    ME: `${import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1'}/auth/me`,
    PERMISSIONS_CHECK: `${import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1'}/auth/check-permissions`,
    REFRESH: `${import.meta.env.VITE_UAU_AUTH_URL || 'https://uau.surdataconsultores.cl/api/v1'}/auth/refresh`
  },

  STORAGE_KEYS: {
    TOKEN: 'camping_token',
    REFRESH_TOKEN: 'camping_refresh_token',
    USER: 'camping_user',
    PERMISSIONS: 'camping_permissions',
    EMPRESA_ACTIVA: 'camping_empresa_activa'
  },

  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
} as const;

// ====================================
// RUTAS DE LA APLICACIÓN
// ====================================

export const ROUTES = {
  // Auth
  LOGIN: '/login',

  // Home
  HOME: '/',
  RESERVAR: '/reservar',
  CHECKOUT: '/checkout',
  MIS_RESERVAS: '/mis-reservas',

  // Admin
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_ESPACIOS: '/admin/espacios',
  ADMIN_RESERVAS: '/admin/reservas',
  ADMIN_CALENDARIO: '/admin/calendario',
  ADMIN_REPORTES: '/admin/reportes',
  ADMIN_CONFIGURACION: '/admin/configuracion',
} as const;

// ====================================
// CONSTANTES DE PERMISOS
// ====================================

export const PERMISOS = {
  // Admin
  ADMIN_ADMINISTRAR: 'camping.admin.administrar',

  // Espacios
  ESPACIOS_VER: 'camping.espacios.ver',
  ESPACIOS_CREAR: 'camping.espacios.crear',
  ESPACIOS_EDITAR: 'camping.espacios.editar',
  ESPACIOS_ELIMINAR: 'camping.espacios.eliminar',

  // Reservas
  RESERVAS_VER: 'camping.reservas.ver',
  RESERVAS_CREAR: 'camping.reservas.crear',
  RESERVAS_EDITAR: 'camping.reservas.editar',
  RESERVAS_ELIMINAR: 'camping.reservas.eliminar',
  RESERVAS_CONFIRMAR: 'camping.reservas.confirmar',

  // DTEs (Documentos Tributarios)
  DTES_VER: 'camping.dtes.ver',
  DTES_CREAR: 'camping.dtes.crear',

  // Reportes
  REPORTES_VER: 'camping.reportes.ver',
  REPORTES_EXPORTAR: 'camping.reportes.exportar',
} as const;

// ====================================
// CONSTANTES DE ESTADO
// ====================================

export const ESTADOS_RESERVA = {
  EN_PROCESO: 'en_proceso',
  CONFIRMADA: 'confirmada',
  CANCELADA: 'cancelada',
} as const;

export const ESTADOS_PAGO = {
  PENDIENTE: 'pendiente',
  PAGADO: 'pagado',
  REEMBOLSADO: 'reembolsado',
} as const;

export const LABELS_ESTADO_RESERVA = {
  [ESTADOS_RESERVA.EN_PROCESO]: 'En Proceso',
  [ESTADOS_RESERVA.CONFIRMADA]: 'Confirmada',
  [ESTADOS_RESERVA.CANCELADA]: 'Cancelada',
} as const;

export const COLORES_ESTADO_RESERVA = {
  [ESTADOS_RESERVA.EN_PROCESO]: 'text-yellow-600 bg-yellow-100',
  [ESTADOS_RESERVA.CONFIRMADA]: 'text-green-600 bg-green-100',
  [ESTADOS_RESERVA.CANCELADA]: 'text-red-600 bg-red-100',
} as const;

// ====================================
// QUERY KEYS (React Query)
// ====================================

export const QUERY_KEYS = {
  // Espacios
  ESPACIOS: ['espacios'],
  ESPACIO: (id: string) => ['espacios', id],
  ESPACIOS_SEARCH: (filters: any) => ['espacios', 'search', filters],

  // Disponibilidad
  DISPONIBILIDAD: ['disponibilidad'],
  DISPONIBILIDAD_SEARCH: (filters: any) => ['disponibilidad', 'search', filters],

  // Reservas
  RESERVAS: ['reservas'],
  RESERVA: (id: string) => ['reservas', id],
  MIS_RESERVAS: ['reservas', 'mias'],

  // Usuario
  USER_PROFILE: ['user', 'profile'],
  USER_PERMISSIONS: ['user', 'permissions'],

  // Dashboard
  DASHBOARD_STATS: ['dashboard', 'stats'],
} as const;

// ====================================
// MENSAJES DEL SISTEMA
// ====================================

export const MESSAGES = {
  // Éxito
  SUCCESS: {
    RESERVA_CREATED: 'Reserva creada exitosamente',
    RESERVA_CONFIRMED: 'Reserva confirmada exitosamente',
    RESERVA_CANCELLED: 'Reserva cancelada exitosamente',
    ESPACIO_CREATED: 'Espacio creado exitosamente',
    ESPACIO_UPDATED: 'Espacio actualizado exitosamente',
    ESPACIO_DELETED: 'Espacio eliminado exitosamente',
    LOGIN_SUCCESS: 'Sesión iniciada correctamente',
    LOGOUT_SUCCESS: 'Sesión cerrada correctamente',
  },

  // Error
  ERROR: {
    GENERIC: 'Ha ocurrido un error inesperado',
    NETWORK: 'Error de conexión. Verifica tu internet',
    UNAUTHORIZED: 'No tienes permisos para realizar esta acción',
    SESSION_EXPIRED: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente',
    VALIDATION: 'Por favor, corrige los errores en el formulario',
    FILE_TOO_LARGE: `El archivo es demasiado grande. Máximo ${APP_CONFIG.max_file_size_mb}MB`,
    FILE_INVALID_TYPE: 'Tipo de archivo no válido',
    REQUIRED_FIELD: 'Este campo es obligatorio',
    INVALID_EMAIL: 'Email inválido',
    INVALID_DATE: 'Fecha inválida',
    CONFLICTING_DATES: 'Las fechas entran en conflicto con otra reserva',
  },

  // Confirmación
  CONFIRM: {
    DELETE_ESPACIO: '¿Estás seguro de que deseas eliminar este espacio?',
    CANCEL_RESERVA: '¿Estás seguro de que deseas cancelar esta reserva?',
    LOGOUT: '¿Estás seguro de que deseas cerrar sesión?',
  },

  // Vacío
  EMPTY: {
    ESPACIOS: 'No hay espacios para mostrar',
    RESERVAS: 'No hay reservas para mostrar',
    SEARCH_RESULTS: 'No se encontraron resultados',
  }
} as const;

// ====================================
// CONFIGURACIÓN DE ARCHIVO
// ====================================

export const FILE_CONFIG = {
  MAX_SIZE_MB: APP_CONFIG.max_file_size_mb,
  MAX_SIZE_BYTES: APP_CONFIG.max_file_size_mb * 1024 * 1024,
  ALLOWED_TYPES: APP_CONFIG.allowed_file_types,
  IMAGE_MIME_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ]
} as const;

// ====================================
// CONFIGURACIÓN DE PAGINACIÓN
// ====================================

export const PAGINATION_CONFIG = {
  DEFAULT_PAGE: 1,
  DEFAULT_SIZE: 20,
  SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_SIZE: 100
} as const;

// ====================================
// CONFIGURACIÓN DE FORMULARIO
// ====================================

export const FORM_CONFIG = {
  // Debounce para búsquedas
  SEARCH_DEBOUNCE_MS: 500,

  // Validaciones
  NOMBRE_MAX_LENGTH: 255,
  DESCRIPCION_MAX_LENGTH: 1000,
  OBSERVACIONES_MAX_LENGTH: 500,

  // Tarifas
  TARIFA_MIN: 0,
  TARIFA_MAX: 999999,
  TARIFA_DECIMALES: 0
} as const;

// ====================================
// CONFIGURACIÓN DE UI
// ====================================

export const UI_CONFIG = {
  // Animaciones
  TRANSITION_DURATION: 200,
  DEBOUNCE_MS: 300,

  // Breakpoints (Tailwind)
  BREAKPOINTS: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
  },

  // Toast
  TOAST_DURATION: 4000,
  TOAST_ERROR_DURATION: 6000,

  // Modal
  MODAL_BACKDROP_CLASSES: 'fixed inset-0 bg-black bg-opacity-50 z-40',
  MODAL_CONTENT_CLASSES: 'fixed inset-0 z-50 overflow-y-auto',

  // Loading
  LOADING_SPINNER_SIZE: {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  }
} as const;

// ====================================
// CONFIGURACIÓN DE DESARROLLO
// ====================================

export const DEV_CONFIG = {
  ENABLE_QUERY_DEV_TOOLS: APP_CONFIG.debug,
  ENABLE_REACT_DEV_TOOLS: APP_CONFIG.debug,
  LOG_LEVEL: APP_CONFIG.debug ? 'debug' : 'error',

  // Mock data
  ENABLE_MOCK_API: false,
  MOCK_DELAY_MS: 1000,

  // Testing
  TEST_USER: {
    username: 'admin',
    password: 'admin123'
  }
} as const;

// ====================================
// UTILIDADES
// ====================================

export const createRoute = (path: string, params?: Record<string, string | number>) => {
  let route = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      route = route.replace(`:${key}`, String(value));
    });
  }
  return route;
};

export const getQueryKey = (key: string, params?: any) => {
  return params ? [key, params] : [key];
};

export const isProduction = import.meta.env.PROD;
export const isDevelopment = import.meta.env.DEV;
